<template>
  <main class="cb-page leaderboard-screen">
    <header class="leaderboard-screen__header"><h1>Лидерборд</h1></header>

    <section class="leaderboard-cover" aria-label="Каждая победа — шаг выше">
      <img :src="coverUrl" width="1120" height="431" alt="" decoding="async" />
      <h2>Каждая<br />победа —<br /><strong>шаг выше</strong></h2>
    </section>

    <section class="leaderboard-panel leaderboard-place" aria-label="Ваша позиция в рейтинге" :aria-busy="contextLoading ? 'true' : undefined">
      <div>
        <h2>Ваше место</h2>
        <p v-if="mine">{{ mine.playerName }} · {{ mine.wins }} {{ pluralRu(mine.wins, 'победа', 'победы', 'побед') }}</p>
        <p v-else>{{ contextLoading ? 'Обновляем рейтинг…' : 'Позиция недоступна' }}</p>
        <button v-if="contextError" type="button" class="leaderboard-place__retry" @click="loadContext">Повторить загрузку места</button>
      </div>
      <strong class="leaderboard-place__rank cb-num">{{ contextLoading ? '…' : mine && mine.rank ? `#${mine.rank}` : '—' }}</strong>
    </section>

    <section class="leaderboard-panel" aria-label="Лидеры">
      <RatingTable v-if="state === 'loading' || rows.length" :rows="rows" :my-user-id="session.userId" :loading="state === 'loading'" label="Лидеры" />
      <div v-if="state === 'error'" class="leaderboard-state" role="alert">
        <p>Не удалось загрузить лидеров</p><p class="leaderboard-state__detail">{{ listError }}</p>
        <AppButton variant="ghost" size="sm" @click="loadLeaders">Повторить</AppButton>
      </div>
      <p v-else-if="state === 'ready' && !rows.length" class="leaderboard-state">Пока никто не сыграл. Сыграйте первую дуэль!</p>
      <div v-if="state === 'ready' && hasMore" class="leaderboard-more">
        <p v-if="listError" class="leaderboard-state__detail" role="alert">{{ listError }}</p>
        <AppButton variant="ghost" size="sm" :loading="loadingMore" block @click="loadMore">{{ listError ? 'Повторить загрузку' : 'Ещё игроки' }}</AppButton>
      </div>
    </section>

    <section v-if="!mineInLeaders" class="leaderboard-nearby" aria-labelledby="nearby-title">
      <h2 id="nearby-title">Рядом с вами</h2>
      <div class="leaderboard-panel">
        <RatingTable v-if="contextLoading || visibleNearby.length" :rows="visibleNearby" :my-user-id="session.userId" :loading="contextLoading" :head="false" label="Игроки рядом с вами" />
        <div v-else-if="contextError" class="leaderboard-state" role="alert">
          <p>Не удалось загрузить вашу позицию</p>
          <AppButton variant="ghost" size="sm" @click="loadContext">Повторить</AppButton>
        </div>
        <p v-else class="leaderboard-state">{{ mine && mine.rank ? 'Соседние позиции пока недоступны' : 'Сыграйте первую игру, чтобы попасть в рейтинг' }}</p>
      </div>
    </section>

    <footer class="leaderboard-screen__footer">
      <AppButton variant="secondary" size="md" block @click="router.push('/')">Главное меню</AppButton>
    </footer>
  </main>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import AppButton from "@/components/ui/AppButton.vue";
import { getRating } from "@/services/api";
import { normalizeRatingRow } from "@/utils/player";
import { pluralRu } from "@/utils/format";
import { useSessionStore } from "@/stores/session";
import RatingTable from "./main/RatingTable.vue";
import { useRatingContext } from "./main/useRatingContext";
import coverUrl from "@/assets/img/layout-pack/leaderboard-cover.jpg";

const router = useRouter();
const session = useSessionStore();
const { mine, nearby, loading: contextLoading, error: contextError, load: loadContext } = useRatingContext(session, 2);
const state = ref("loading");
const rows = ref([]);
const loadedUserIds = computed(() => new Set(rows.value.filter((row) => row.userId !== null).map((row) => Number(row.userId))));
const mineInLeaders = computed(() => session.userId !== null && session.userId !== undefined && loadedUserIds.value.has(Number(session.userId)));
// Preserve the authoritative neighbours, without repeating already visible players.
const visibleNearby = computed(() => nearby.value.filter((row) => row.userId === null || !loadedUserIds.value.has(Number(row.userId))));
const listError = ref("");
const hasMore = ref(false);
const loadingMore = ref(false);
let request = 0;
let nextOffset = 0;
let disposed = false;

// Show the three leaders first, leaving the player's actual neighbours in view.
async function loadLeaders() {
  const current = ++request;
  state.value = "loading";
  listError.value = "";
  try {
    const response = await getRating(3, 0);
    if (disposed || current !== request) return;
    const list = Array.isArray(response) ? response : [];
    rows.value = list.map((row, index) => normalizeRatingRow(row, index)).filter(Boolean);
    nextOffset = list.length;
    hasMore.value = list.length === 3;
    state.value = "ready";
  } catch (error) {
    if (disposed || current !== request) return;
    listError.value = (error && error.message) || "Попробуйте ещё раз";
    state.value = "error";
  }
}

async function loadMore() {
  if (loadingMore.value || !hasMore.value) return;
  loadingMore.value = true;
  listError.value = "";
  const current = request;
  const offset = nextOffset;
  try {
    const response = await getRating(25, offset);
    if (disposed || current !== request) return;
    const list = Array.isArray(response) ? response : [];
    const page = list.map((row, index) => normalizeRatingRow(row, offset + index)).filter(Boolean);
    const loadedIds = new Set(rows.value.filter((row) => row.userId !== null).map((row) => row.userId));
    // A live rating can change between requests; don't duplicate a player crossing a page boundary.
    rows.value.push(...page.filter((row) => row.userId === null || !loadedIds.has(row.userId)));
    nextOffset += list.length;
    hasMore.value = list.length === 25;
  } catch (error) {
    if (!disposed && current === request) listError.value = (error && error.message) || "Попробуйте ещё раз";
  } finally {
    if (!disposed && current === request) loadingMore.value = false;
  }
}

onMounted(() => {
  loadLeaders();
  loadContext();
  if (!session.me) session.loadMe().catch(() => null);
});
onBeforeUnmount(() => { disposed = true; request += 1; });
</script>

<style scoped>
.leaderboard-screen { max-width: 460px; margin-inline: auto; gap: 14px; --cb-text-2: #b6bab1; }
.leaderboard-screen__header { padding: 5px 0 2px; }
.leaderboard-screen__header h1 { font-size: 28px; line-height: 1.15; letter-spacing: -.025em; }
.leaderboard-cover { position: relative; height: 112px; overflow: hidden; border-radius: 16px; border: 1px solid var(--cb-border-strong); background: var(--cb-surface); }
.leaderboard-cover img { width: 100%; height: 100%; object-fit: cover; }
.leaderboard-cover h2 { position: absolute; left: 14px; top: 50%; width: 44%; transform: translateY(-50%); font-size: 19px; line-height: 1.1; font-weight: 750; letter-spacing: -.015em; text-shadow: 0 2px 10px rgba(0,0,0,.4); }
.leaderboard-cover strong { color: var(--cb-lime); }
.leaderboard-panel { border: 1px solid var(--cb-border-strong); border-radius: 16px; background: linear-gradient(150deg,rgba(255,255,255,.035),rgba(255,255,255,.012)); overflow: hidden; }
.leaderboard-place { display: flex; align-items: center; justify-content: space-between; gap: 12px; min-height: 72px; padding: 14px; }
.leaderboard-place > div { min-width: 0; }
.leaderboard-place h2 { font-size: 16px; font-weight: 650; margin-bottom: 3px; }
.leaderboard-place p { font-size: 13px; color: var(--cb-text-2); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.leaderboard-place__rank { flex-shrink: 0; color: var(--cb-lime); font-size: clamp(28px,8vw,34px); line-height: 1.1; }
.leaderboard-place__retry { min-height: 44px; padding: 5px 0; border: 0; background: transparent; color: var(--cb-lime); font: 500 12px/1.3 var(--cb-font); cursor: pointer; text-align: left; }
.leaderboard-screen :deep(.leaderboard-row) { min-height: 58px; padding-block: 10px; }
.leaderboard-screen :deep(.leaderboard-row--me) { border-radius: 0; box-shadow: none; }
.leaderboard-screen :deep(.leaderboard-row--me::before) { border-radius: 0; }
.leaderboard-nearby { padding-top: 8px; border-top: 1px solid var(--cb-border-strong); }
.leaderboard-nearby > h2 { margin: 0 4px 10px; font-size: 14px; font-weight: 550; color: var(--cb-text-2); }
.leaderboard-state { padding: 16px; font-size: 14px; color: var(--cb-text-2); }
.leaderboard-state__detail { margin-top: 6px; font-size: 12px; color: var(--cb-text-2); }
.leaderboard-more { border-top: 1px solid var(--cb-border); padding: 2px 8px; text-align: center; }
.leaderboard-more .app-button { min-height: 44px; font-size: 13px; }
.leaderboard-screen__footer { margin-top: auto; padding-top: 6px; }
.leaderboard-screen__footer .app-button { height: 48px; border-radius: 14px; font-size: 15px; box-shadow: none; font-weight: 550; }
@media (max-width: 350px) { .leaderboard-cover { height: 102px; } .leaderboard-cover h2 { font-size: 17px; } }
</style>
